import React, { useState, useEffect, useCallback } from 'react';
import { Plus, BarChart3, Check, Edit2, Trash2, Settings, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default categories - can be customized per building
const defaultCategories = [
  { value: 'administration', label: 'Administration', icon: '📋' },
  { value: 'assurance', label: 'Assurance', icon: '🛡️' },
  { value: 'entretien_parties_communes', label: 'Entretien parties communes', icon: '🧹' },
  { value: 'honoraires_syndic', label: 'Honoraires syndic', icon: '💼' },
  { value: 'ascenseur', label: 'Ascenseur', icon: '🛗' },
  { value: 'chauffage', label: 'Chauffage', icon: '🔥' },
  { value: 'eau_froide', label: 'Eau froide', icon: '💧' },
  { value: 'eau_chaude', label: 'Eau chaude', icon: '🚿' },
  { value: 'electricite', label: 'Électricité', icon: '⚡' },
  { value: 'espaces_verts', label: 'Espaces verts', icon: '🌳' },
  { value: 'gardiennage', label: 'Gardiennage', icon: '👮' },
  { value: 'nettoyage', label: 'Nettoyage', icon: '🧽' },
  { value: 'interphone_digicode', label: 'Interphone / Digicode', icon: '🔔' },
  { value: 'travaux_entretien', label: 'Travaux entretien', icon: '🔧' },
  { value: 'provisions_travaux', label: 'Provisions travaux', icon: '🏗️' },
  { value: 'frais_bancaires', label: 'Frais bancaires', icon: '🏦' },
  { value: 'autres', label: 'Autres', icon: '📦' }
];

export default function BudgetSection({ buildingId, exerciceId, token }) {
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(false);

  // Fetch custom categories for this building
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comptabilite/categories?building_id=${buildingId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setCategories(data);
        } else {
          setCategories(defaultCategories);
        }
      } else {
        setCategories(defaultCategories);
      }
    } catch (error) {
      setCategories(defaultCategories);
    }
  }, [buildingId, token]);

  const fetchBudget = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comptabilite/budgets?building_id=${buildingId}&exercice_id=${exerciceId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const detailResponse = await fetch(
            `${API_URL}/api/comptabilite/budgets/${data[0].id}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (detailResponse.ok) {
            setBudget(await detailResponse.json());
          }
        }
      }
    } catch (error) {
      console.error('Error fetching budget:', error);
    } finally {
      setLoading(false);
    }
  }, [buildingId, exerciceId, token]);

  useEffect(() => {
    fetchCategories();
    fetchBudget();
  }, [fetchCategories, fetchBudget]);

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getEcartColor = (ecart, prevu) => {
    if (prevu === 0) return 'text-gray-500';
    const pct = (ecart / prevu) * 100;
    if (pct > 10) return 'text-green-600';
    if (pct < -10) return 'text-red-600';
    return 'text-yellow-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  if (!budget) {
    return (
      <div className="space-y-4">
        {/* Categories Management Button */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowCategoriesModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            data-testid="manage-categories-btn"
          >
            <Settings className="h-4 w-4" />
            Gérer les catégories
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun budget défini</h3>
          <p className="text-gray-500 mb-4">Créez votre budget prévisionnel pour cet exercice</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6]"
            data-testid="create-budget-btn"
          >
            <Plus className="h-5 w-5" />
            Créer le budget
          </button>
        </div>

        {showCreateModal && (
          <CreateBudgetModal
            buildingId={buildingId}
            exerciceId={exerciceId}
            token={token}
            categories={categories}
            onClose={() => setShowCreateModal(false)}
            onCreated={() => {
              setShowCreateModal(false);
              fetchBudget();
            }}
          />
        )}

        {showCategoriesModal && (
          <CategoriesModal
            buildingId={buildingId}
            token={token}
            categories={categories}
            onClose={() => setShowCategoriesModal(false)}
            onSaved={(newCategories) => {
              setCategories(newCategories);
              setShowCategoriesModal(false);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{budget.libelle}</h2>
          <p className="text-sm text-gray-500">
            {budget.valide ? '✓ Validé' : 'En attente de validation'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCategoriesModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            data-testid="manage-categories-btn"
          >
            <Settings className="h-4 w-4" />
            Catégories
          </button>
          {!budget.valide && (
            <button
              onClick={() => setEditing(!editing)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <Edit2 className="h-4 w-4" />
              {editing ? 'Terminer' : 'Modifier'}
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">Budget prévu</p>
          <p className="text-2xl font-bold text-gray-900">{formatMoney(budget.total_prevu)}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">Réalisé</p>
          <p className="text-2xl font-bold text-blue-600">{formatMoney(budget.total_realise)}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">Écart</p>
          <p className={`text-2xl font-bold ${budget.ecart_global >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {budget.ecart_global >= 0 ? '+' : ''}{formatMoney(budget.ecart_global)}
          </p>
        </div>
      </div>

      {/* Budget Lines Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prévu</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Réalisé</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Écart</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {budget.lignes?.map((ligne, index) => {
              const cat = categories.find(c => c.value === ligne.categorie);
              return (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{cat?.icon || '📦'}</span>
                      <div>
                        <span className="font-medium text-gray-900">{ligne.libelle}</span>
                        <span className="text-xs text-gray-400 ml-2">({ligne.categorie})</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">{formatMoney(ligne.montant_prevu)}</td>
                  <td className="px-4 py-3 text-right">{formatMoney(ligne.montant_realise)}</td>
                  <td className={`px-4 py-3 text-right ${getEcartColor(ligne.ecart, ligne.montant_prevu)}`}>
                    {ligne.ecart >= 0 ? '+' : ''}{formatMoney(ligne.ecart)}
                  </td>
                  <td className={`px-4 py-3 text-right ${getEcartColor(ligne.ecart, ligne.montant_prevu)}`}>
                    {ligne.ecart_pourcentage >= 0 ? '+' : ''}{ligne.ecart_pourcentage || 0}%
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 font-semibold">
            <tr>
              <td className="px-4 py-3">TOTAL</td>
              <td className="px-4 py-3 text-right">{formatMoney(budget.total_prevu)}</td>
              <td className="px-4 py-3 text-right">{formatMoney(budget.total_realise)}</td>
              <td className={`px-4 py-3 text-right ${budget.ecart_global >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {budget.ecart_global >= 0 ? '+' : ''}{formatMoney(budget.ecart_global)}
              </td>
              <td className="px-4 py-3 text-right">-</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Validate Button */}
      {!budget.valide && (
        <div className="flex justify-end">
          <button
            onClick={async () => {
              try {
                await fetch(`${API_URL}/api/comptabilite/budgets/${budget.id}/valider`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({})
                });
                toast.success('Budget validé');
                fetchBudget();
              } catch (error) {
                toast.error('Erreur lors de la validation');
              }
            }}
            className="flex items-center gap-2 px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6]"
          >
            <Check className="h-5 w-5" />
            Valider le budget
          </button>
        </div>
      )}

      {showCategoriesModal && (
        <CategoriesModal
          buildingId={buildingId}
          token={token}
          categories={categories}
          onClose={() => setShowCategoriesModal(false)}
          onSaved={(newCategories) => {
            setCategories(newCategories);
            setShowCategoriesModal(false);
          }}
        />
      )}
    </div>
  );
}

// Categories Management Modal
function CategoriesModal({ buildingId, token, categories: initialCategories, onClose, onSaved }) {
  const [categories, setCategories] = useState([...initialCategories]);
  const [newCategory, setNewCategory] = useState({ value: '', label: '', icon: '📦' });
  const [saving, setSaving] = useState(false);

  const handleAddCategory = () => {
    if (!newCategory.label.trim()) {
      toast.error('Veuillez saisir un nom de catégorie');
      return;
    }
    
    const value = newCategory.value || newCategory.label.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '_');
    
    if (categories.find(c => c.value === value)) {
      toast.error('Cette catégorie existe déjà');
      return;
    }
    
    setCategories([...categories, { ...newCategory, value }]);
    setNewCategory({ value: '', label: '', icon: '📦' });
  };

  const handleRemoveCategory = (value) => {
    setCategories(categories.filter(c => c.value !== value));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          building_id: buildingId,
          categories: categories
        })
      });

      if (response.ok) {
        toast.success('Catégories enregistrées');
        onSaved(categories);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const commonIcons = ['📋', '🛡️', '🧹', '💼', '🛗', '🔥', '💧', '🚿', '⚡', '🌳', '👮', '🧽', '🔔', '🔧', '🏗️', '🏦', '📦', '🚗', '🔒', '📡', '🎥', '🗑️'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Gérer les catégories de charges</h3>
            <p className="text-sm text-gray-500">Personnalisez les catégories pour ce bâtiment</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Add new category */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Ajouter une catégorie</h4>
            <div className="flex gap-3">
              <div className="relative">
                <select
                  value={newCategory.icon}
                  onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                  className="w-16 h-10 px-2 border border-gray-200 rounded-lg text-center text-xl appearance-none cursor-pointer"
                >
                  {commonIcons.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                value={newCategory.label}
                onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
                placeholder="Nom de la catégorie"
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg"
              />
              <button
                onClick={handleAddCategory}
                className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6]"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Categories list */}
          <div className="space-y-2">
            {categories.map((category, index) => (
              <div
                key={category.value}
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{category.icon}</span>
                  <div>
                    <span className="font-medium text-gray-900">{category.label}</span>
                    <span className="text-xs text-gray-400 ml-2">({category.value})</span>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveCategory(category.value)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {categories.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Aucune catégorie. Ajoutez-en une ci-dessus.
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-gray-100 flex justify-between">
          <button
            onClick={() => setCategories([...defaultCategories])}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Réinitialiser par défaut
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Create Budget Modal
function CreateBudgetModal({ buildingId, exerciceId, token, categories, onClose, onCreated }) {
  const [lignes, setLignes] = useState(
    categories.map(c => ({
      categorie: c.value,
      libelle: c.label,
      icon: c.icon,
      montant_prevu: 0,
      clef_repartition: 'generale'
    }))
  );
  const [saving, setSaving] = useState(false);

  const handleMontantChange = (index, value) => {
    const newLignes = [...lignes];
    newLignes[index].montant_prevu = parseFloat(value) || 0;
    setLignes(newLignes);
  };

  const total = lignes.reduce((sum, l) => sum + (l.montant_prevu || 0), 0);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/budgets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          building_id: buildingId,
          exercice_id: exerciceId,
          annee: new Date().getFullYear(),
          lignes: lignes.filter(l => l.montant_prevu > 0).map(l => ({
            categorie: l.categorie,
            libelle: l.libelle,
            montant_prevu: l.montant_prevu,
            clef_repartition: l.clef_repartition
          }))
        })
      });

      if (response.ok) {
        toast.success('Budget créé');
        onCreated();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold">Créer le budget prévisionnel</h3>
          <p className="text-sm text-gray-500">Saisissez les montants prévisionnels par catégorie</p>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-3">
            {lignes.map((ligne, index) => (
              <div key={ligne.categorie} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <span className="text-xl w-8 text-center">{ligne.icon}</span>
                <span className="flex-1 font-medium text-gray-900">{ligne.libelle}</span>
                <div className="relative">
                  <input
                    type="number"
                    value={ligne.montant_prevu || ''}
                    onChange={(e) => handleMontantChange(index, e.target.value)}
                    placeholder="0"
                    className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-right pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold text-gray-900">Total prévisionnel</span>
            <span className="text-2xl font-bold text-[#00D3A8]">{formatMoney(total)}</span>
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || total === 0}
              className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Créer le budget
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
